// Radha — one money formatter for the whole app (Phase 1 of globalization).
//
// Replaces 19 hand-rolled `inr()` copies that each hard-coded a ₹ symbol and
// India's crore/lakh folding. Because migration 010 gave every money-bearing
// table an ISO-4217 `currency` column, those formatters were not merely
// India-flavoured — they were WRONG: a USD invoice rendered as "₹27L".
//
// The Indian compact scale is not a special case that needs bespoke code. ICU
// already knows it: Intl.NumberFormat("en-IN", {notation:"compact"}) yields
// "6.17Cr" while "en-US" yields "6.17M" from the same number. So the tenant's
// BCP-47 locale (063) does the work, and Indian tenants keep the exact display
// they have today.
//
// Context is module-level rather than a React context on purpose: the 19 call
// sites are plain function calls inside table-row loops, and threading a hook
// through all of them would be a much larger, riskier change. DirectShell sets
// the context during its own render, which React guarantees runs before its
// children's — so panels always format with the current tenant's settings.

export interface MoneyContext {
  locale: string;        // BCP-47, e.g. "en-IN", "de-DE", "ar-AE"
  baseCurrency: string;  // ISO-4217 fallback when a row carries no currency
  // Presentation translation (reporting currency). When set, every figure is
  // converted into it for DISPLAY only — the book, the stored amounts and the
  // exports keep their own units. rates are USD-anchored units-per-USD, so a
  // cross rate is one division; a missing pair leaves the amount unconverted
  // rather than inventing a number.
  displayCurrency: string | null;
  rates: Record<string, number>;
  // Dates follow the COUNTRY, money follows the currency: pinning USD should
  // not silently reformat every date as mm/dd. Kept separate for that reason.
  dateLocale: string;
}

// Defaults keep pre-063 behaviour until the tenant loads, so nothing flashes
// a different currency on first paint.
let ctx: MoneyContext = {
  locale: "en-IN", baseCurrency: "INR", displayCurrency: null, rates: {}, dateLocale: "en-IN",
};

export function setMoneyContext(next: Partial<MoneyContext>): void {
  const locale = next.locale || ctx.locale;
  const baseCurrency = next.baseCurrency || ctx.baseCurrency;
  const displayCurrency = next.displayCurrency === undefined ? ctx.displayCurrency : next.displayCurrency;
  const rates = next.rates ?? ctx.rates;
  const dateLocale = next.dateLocale || ctx.dateLocale;
  if (locale === ctx.locale && baseCurrency === ctx.baseCurrency
      && displayCurrency === ctx.displayCurrency && rates === ctx.rates
      && dateLocale === ctx.dateLocale) return;
  ctx = { locale, baseCurrency, displayCurrency, rates, dateLocale };
}

/**
 * Translate one amount into the reporting currency. Returns the pair
 * [amount, currency] actually to be shown — unchanged when no reporting
 * currency is set, or when either leg has no quote.
 */
function present(n: number, currency: string): [number, string] {
  const target = ctx.displayCurrency;
  if (!target || target === currency) return [n, currency];
  const from = ctx.rates[currency], to = ctx.rates[target];
  if (!from || !to) return [n, currency];   // no quote: never fabricate one
  return [(n / from) * to, target];
}

export function getMoneyContext(): MoneyContext {
  return ctx;
}

/** The tenant's own book currency — what a typed-in amount will be stored as. */
export function homeCurrency(): string {
  return ctx.baseCurrency;
}

/**
 * Money in its OWN currency, never translated. For panels whose subject IS
 * the currency — FX exposure, revaluation — where converting an AED balance
 * into EUR would erase the very fact being reported.
 */
export function fmtMoneyNative(
  value: string | number | null | undefined,
  currency: string,
  compact = true,
): string {
  const n = toNum(value);
  if (n === null) return "—";
  if (compact && Math.abs(n) >= COMPACT_FLOOR) {
    return nf(ctx.locale, {
      style: "currency", currency, notation: "compact", maximumFractionDigits: 2,
    }).format(n);
  }
  return nf(ctx.locale, { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
}

// Intl.NumberFormat construction is genuinely expensive and these run inside
// loops over hundreds of invoice rows, so formatters are memoised by their
// full option signature.
const cache = new Map<string, Intl.NumberFormat>();
function nf(locale: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + "|" + JSON.stringify(opts);
  let f = cache.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(locale, opts);
    } catch {
      // An unknown locale or a currency code the runtime rejects must not take
      // a financial table down — fall back to a plain decimal.
      f = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
    }
    cache.set(key, f);
  }
  return f;
}

const toNum = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
};

// Compact below this and the output stops reading like money to a finance
// team. Verified against ICU: en-IN compacts 98,244 to "₹98.24K", but Indian
// accounting convention has no thousands unit — the old inr() correctly showed
// "₹98,244" and anything else looks wrong to an AR analyst in Pune. 1e5 is also
// a sane cut elsewhere ("$617K" compacts, "$61,700" stays exact), so one
// threshold serves every locale without special-casing India.
const COMPACT_FLOOR = 100_000;

/**
 * Compact money — the direct replacement for the old `inr()`.
 * en-IN → "₹6.17Cr" / "₹6.17L" / "₹98,244"; en-US → "$6.17M" / "$617K";
 * de-DE → "6,17 Mio. €"; ar-AE → "٦٫١٧ مليون د.إ.‏" (RTL-marked by ICU).
 */
export function fmtMoney(
  value: string | number | null | undefined,
  currency?: string | null,
): string {
  const raw = toNum(value);
  if (raw === null) return "—";
  const [n, cur] = present(raw, currency || ctx.baseCurrency);
  if (Math.abs(n) < COMPACT_FLOOR) {
    return nf(ctx.locale, {
      style: "currency", currency: cur, maximumFractionDigits: 0,
    }).format(n);
  }
  return nf(ctx.locale, {
    style: "currency", currency: cur, notation: "compact", maximumFractionDigits: 2,
  }).format(n);
}

/** Exact money, grouped, no compaction — for statements and single figures. */
export function fmtMoneyFull(
  value: string | number | null | undefined,
  currency?: string | null,
): string {
  const raw = toNum(value);
  if (raw === null) return "—";
  const [n, cur] = present(raw, currency || ctx.baseCurrency);
  return nf(ctx.locale, {
    style: "currency", currency: cur, maximumFractionDigits: 0,
  }).format(n);
}

/** Exact money keeping minor units — for tax lines and rates. */
export function fmtMoneyPrecise(
  value: string | number | null | undefined,
  currency?: string | null,
): string {
  const raw = toNum(value);
  if (raw === null) return "—";
  const [n, cur] = present(raw, currency || ctx.baseCurrency);
  return nf(ctx.locale, {
    style: "currency", currency: cur,
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Convert one foreign-currency amount to the tenant's base before it is added
 * to anything. Adding an AED invoice to an INR one because both are NUMERIC is
 * the easiest way to produce a confidently wrong total; fx_rate_at_issue is the
 * carrying rate (IAS 21), so this is the balance as booked — retranslation is
 * reported separately by the revaluation panel (071).
 */
export function toBase(
  amount: string | number | null | undefined,
  fxRateAtIssue: string | number | null | undefined,
): number {
  return (toNum(amount) ?? 0) * (toNum(fxRateAtIssue) ?? 1);
}

/** Locale-aware plain number (counts, day tallies). */
export function fmtNumber(value: string | number | null | undefined): string {
  const n = toNum(value);
  if (n === null) return "—";
  return nf(ctx.locale, { maximumFractionDigits: 2 }).format(n);
}

/** Locale-aware date — dd/mm/yyyy in en-IN and de-DE, mm/dd/yyyy in en-US. */
export function fmtDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(ctx.dateLocale, { dateStyle: "medium" }).format(d);
}

/** Short date for axis ticks and dense rows — "13 Aug" / "Aug 13". */
export function fmtDateShort(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(ctx.dateLocale, { day: "numeric", month: "short" }).format(d);
}

/** Date + time, for audit trails and run logs. */
export function fmtDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(ctx.dateLocale, { dateStyle: "medium", timeStyle: "short" }).format(d);
}
